// Promise-based client for the engine worker, with a transparent main-thread
// fallback. Everything that needs the engine calls compare() and never has to
// know whether a Worker is available — the fallback runs the same engine code, so
// tests and any Worker-less environment behave identically.
import { compare as runCompare } from '../engine/index.js';
import { trackClientError } from './analytics.js';
import { readBinaryInput } from './file-reader.js';

let workerRef; // undefined = not yet resolved, null = unavailable

function getWorker() {
  if (workerRef !== undefined) return workerRef;
  if (typeof Worker === 'undefined') {
    workerRef = null;
    return null;
  }
  try {
    workerRef = new Worker(new URL('../workers/engine.worker.js', import.meta.url), {
      type: 'module',
    });
  } catch {
    workerRef = null;
    trackClientError('worker_spawn_fail');
  }
  return workerRef;
}

let seq = 0;
const TIMEOUT_MS = 30_000; // safety net for a hung/crashed worker — generous so a
// genuinely large (but progressing) diff is never killed mid-flight.

// Requests currently in flight ON A GIVEN worker instance. Terminating a worker
// kills every request it was running, so each one must be failed explicitly —
// and a stale request must never take down a *replacement* worker (REL-057).
const pending = new WeakMap(); // Worker -> Set<(err) => void>

function trackRequest(worker, fail) {
  let set = pending.get(worker);
  if (!set) pending.set(worker, (set = new Set()));
  set.add(fail);
  return () => set.delete(fail);
}

// A dead/hung worker must not wedge the app: drop the reference so the next
// compare() spins up a fresh one. `target` scopes the teardown to the worker the
// caller was actually talking to — a late timeout from an already-replaced
// worker must not terminate the live one.
function resetWorker(errorCode, target) {
  const w = target ?? workerRef;
  if (workerRef === w) workerRef = undefined;
  try {
    w?.terminate?.();
  } catch {
    /* already gone */
  }
  // Everything else queued on that worker will never get a message back.
  const waiting = w ? pending.get(w) : null;
  if (waiting) {
    pending.delete(w);
    for (const fail of [...waiting]) fail(new Error('the comparison engine was restarted'));
  }
  if (errorCode) trackClientError(errorCode);
}

/** Run a comparison off the main thread (or on it, if no Worker exists). */
export function compare(payload) {
  return request('compare', payload);
}

export function readBinary(payload, { onProgress } = {}) {
  // filter(Boolean) on both branches: postMessage throws DataCloneError if the
  // transfer list contains undefined, which would kill the worker outright.
  const transfer = (payload.files ? payload.files.map((f) => f.buffer) : [payload.buffer]).filter(Boolean);
  return request('readBinary', payload, transfer, onProgress);
}

function request(type, payload, transfer = [], onProgress) {
  const worker = getWorker();
  if (!worker) {
    return Promise.resolve()
      .then(() => type === 'compare' ? runCompare(payload) : readBinaryInput(payload, onProgress))
      .catch((err) => {
        trackClientError('engine_throw');
        throw err;
      });
  }
  const id = ++seq;
  return new Promise((resolve, reject) => {
    let timer;
    let untrack = () => {};
    const cleanup = () => {
      clearTimeout(timer);
      untrack();
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
    };
    // Invoked when *another* request tears this worker down under us.
    const failWithWorker = (err) => {
      cleanup();
      reject(err);
    };
    const onMessage = (e) => {
      const msg = e.data;
      if (!msg || msg.id !== id) return;
      if (msg.progress) { armTimeout(); onProgress?.(msg.progress); return; }
      cleanup();
      if (msg.ok) resolve(msg.result);
      else {
        trackClientError('engine_throw');
        reject(new Error(msg.error || 'worker error'));
      }
    };
    const onError = (err) => {
      cleanup();
      resetWorker('engine_throw', worker); // the worker errored — recreate it next time
      reject(err instanceof Error ? err : new Error('worker error'));
    };
    const armTimeout = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        cleanup();
        resetWorker('worker_timeout', worker);
        reject(new Error(type === 'readBinary' ? 'File reading timed out. No partial dataset was loaded.' : 'comparison timed out'));
      }, TIMEOUT_MS);
    };
    armTimeout();
    untrack = trackRequest(worker, failWithWorker);
    worker.addEventListener('message', onMessage);
    worker.addEventListener('error', onError);
    try {
      worker.postMessage({ type, id, payload }, transfer);
    } catch (err) {
      cleanup();
      resetWorker('worker_spawn_fail', worker);
      reject(err instanceof Error ? err : new Error('worker post failed'));
    }
  });
}

/** For diagnostics/tests: is a real Worker backing compare()? */
export function isWorkerActive() {
  return getWorker() !== null;
}
